<template>
    <div
        class="flex flex-wrap items-end justify-between gap-4 rounded-xl border border-gray-200 bg-white p-4"
    >
        <div class="flex flex-wrap items-end gap-3">
            <div class="flex flex-col">
                <label
                    class="text-xs font-medium uppercase tracking-wide text-gray-400"
                    for="ai-usage-from"
                >
                    {{ t("vue.kiosk_ai_usage_from") }}
                </label>
                <input
                    id="ai-usage-from"
                    v-model="from"
                    type="date"
                    class="mt-1 rounded-lg border-0 px-3 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
                    @change="applyDates"
                />
            </div>

            <div class="flex flex-col">
                <label
                    class="text-xs font-medium uppercase tracking-wide text-gray-400"
                    for="ai-usage-to"
                >
                    {{ t("vue.kiosk_ai_usage_to") }}
                </label>
                <input
                    id="ai-usage-to"
                    v-model="to"
                    type="date"
                    class="mt-1 rounded-lg border-0 px-3 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
                    @change="applyDates"
                />
            </div>
        </div>

        <div class="flex flex-wrap items-center gap-2">
            <button
                v-for="preset in presets"
                :key="preset"
                type="button"
                class="rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                @click="applyLastDays(preset)"
            >
                {{ preset }} {{ t("vue.kiosk_ai_usage_days_short") }}
            </button>

            <button
                type="button"
                class="rounded-lg px-3 py-1.5 text-sm font-medium shadow-sm ring-1 ring-inset ring-gray-300"
                :class="
                    range.all_time
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                "
                @click="applyAllTime"
            >
                {{ t("vue.kiosk_ai_usage_all_time") }}
            </button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";
import type { IAiUsageRange } from "../types";

const props = defineProps<{
    range: IAiUsageRange;
    routeName: string;
    routeParams: Record<string, number>;
}>();

const { t } = useTranslations();

const presets: number[] = [7, 30, 90];

const from = ref<string>(props.range.from ?? "");
const to = ref<string>(props.range.to ?? "");

watch(
    () => props.range,
    (updated: IAiUsageRange): void => {
        from.value = updated.from ?? "";
        to.value = updated.to ?? "";
    },
);

const visit = (query: Record<string, string | number>): void => {
    router.get(route(props.routeName, props.routeParams), query, {
        preserveScroll: true,
    });
};

const applyDates = (): void => {
    const query: Record<string, string> = {};

    if (from.value !== "") {
        query.from = from.value;
    }

    if (to.value !== "") {
        query.to = to.value;
    }

    visit(query);
};

const toDateString = (date: Date): string => date.toISOString().slice(0, 10);

const applyLastDays = (days: number): void => {
    const start = new Date();
    start.setDate(start.getDate() - days);

    visit({ from: toDateString(start), to: toDateString(new Date()) });
};

const applyAllTime = (): void => {
    visit({ all_time: 1 });
};
</script>
